import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Theme, Tooltip, Typography } from '@mui/material';
import { IoGitMergeOutline, IoGitPullRequestOutline } from 'react-icons/io5';
import MultiLineEllipsis from '../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { EventType } from '../pipeline-summary-types';
import { stopPropagation } from '../../common/common-utils';

const getTooltip = (
  eventType: string,
  sourceBranch: string,
  targetBranch: string,
  pullRequestUrl: string,
  pullRequestId: string,
) => {
  switch (eventType) {
    case EventType.Merge:
      return (
        <Typography
          onClick={stopPropagation}
          variant='caption'
          color='text.secondary'>
          Merge Trigger: Code was merged to <b>{sourceBranch}</b> from a Pull
          Request and then the pipeline scan was triggered.{' '}
          <a href={pullRequestUrl} target='_blank' rel='noreferrer'>
            !{pullRequestId}
          </a>
        </Typography>
      );
    case EventType.PullRequest:
      return (
        <Typography
          onClick={stopPropagation}
          variant='caption'
          color='text.secondary'>
          Pipeline scan was triggered on the creation of a Pull Request. Changes
          were scanned on <b>{sourceBranch}</b> before being merged to{' '}
          <b>{targetBranch}</b>.{' '}
          {pullRequestUrl && (
            <a href={pullRequestUrl} target='_blank' rel='noreferrer'>
              !{pullRequestId}
            </a>
          )}
        </Typography>
      );

    case EventType.Push:
      return (
        <Typography
          onClick={stopPropagation}
          variant='caption'
          color='text.secondary'>
          Push Trigger: Code was directly pushed to <b>{sourceBranch}</b> and
          then the pipeline scan was triggered.{' '}
          {pullRequestUrl && (
            <a href={pullRequestUrl} target='_blank' rel='noreferrer'>
              !{pullRequestId}
            </a>
          )}
        </Typography>
      );
    default:
      return '';
  }
};

const getIcon = (eventType: string) => {
  switch (eventType) {
    case EventType.Merge:
      return <IoGitMergeOutline color='red' />;
    case EventType.PullRequest:
      return <IoGitPullRequestOutline />;
    case EventType.Push:
      return <IoGitMergeOutline />;
    default:
      return null;
  }
};

const BranchColumn: FC<React.PropsWithChildren<BranchColumnProps>> = props => {
  const { classes } = useStyles();
  const {
    eventType,
    sourceBranch,
    targetBranch,
    pullRequestUrl,
    pullRequestId,
  } = props;
  const tooltip = getTooltip(
    eventType,
    sourceBranch,
    targetBranch,
    pullRequestUrl,
    pullRequestId,
  );
  const icon = getIcon(eventType);

  return (
    <MultiLineEllipsis variant='caption' color='text.secondary' lines={2}>
      <Tooltip title={tooltip}>
        <div className={classes?.textCenter}>
          <div onClick={stopPropagation} className={classes.icon}>
            {icon}
          </div>
          {sourceBranch}
        </div>
      </Tooltip>
    </MultiLineEllipsis>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  textCenter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    flexDirection: 'row',
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: theme.spacing(1.75),
  },
}));

export interface BranchColumnProps {
  sourceBranch: string;
  targetBranch: string;
  eventType: string;
  pullRequestUrl: string;
  pullRequestId: string;
}

export default BranchColumn;
